/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IChatContext, TContextProvider } from '../../../interfaces';
import { IProactiveChatTrigger, IWindow } from '../../interfaces';
import { IMsdyn365CsChatConnectorViewProps } from '../../msdyn365-cs-chat-connector';

/**
 * ProactiveChatTrigger class.
 * Checks if proactive chat should be triggered, starts the chat and sets context.
 */
export default abstract class ProactiveChatTrigger implements IProactiveChatTrigger {
    public props: IMsdyn365CsChatConnectorViewProps;
    public context: IChatContext;

    /**
     * Class constructor.
     * @param {IMsdyn365CsChatConnectorViewProps} props The chat connector view properties.
     * @param {IChatContext} context The context.
     */
    constructor(props: IMsdyn365CsChatConnectorViewProps, context: IChatContext) {
        this.props = props;
        this.context = context;
    }

    /**
     * Triggers the proactive chat if proactive chat validation passes.
     * @returns {Promise<boolean>} True if proactive chat is triggered.
     */
    public async startChat(): Promise<boolean> {
        if (await this.validate()) {
            (<IWindow>(<unknown>window)).contextProvider = this.getContextProvider();

            this.props.context.telemetry.debug('Chat widget "lcw:ready" event listener has been triggered for proactive chat.');
            (<IWindow>(<unknown>window)).Microsoft.Omnichannel.LiveChatWidget.SDK.setContextProvider(this.getContextProvider());

            const message = this.getMessage();
            this.props.context.telemetry.debug(`Starting proactive chat with message: '${message}'.`);
            (<IWindow>(<unknown>window)).Microsoft.Omnichannel.LiveChatWidget.SDK.startProactiveChat({ message: message }, false);

            return true;
        } else {
            return false;
        }
    }

    /**
     * Gets the context.
     * @returns {TContextProvider} Chat context.
     */
    public abstract getContextProvider(): TContextProvider;

    /**
     * Generates chat welcome message to be used if proactive chat is triggered.
     * @returns {string} Chat welcome message.
     */
    public abstract getMessage(): string;

    /**
     * Validate specific trigger conditions if proactive chat should be initiated.
     * @returns {Promise<boolean>} True if proactive chat should be triggered.
     */
    public abstract validate(): Promise<boolean>;
}
